import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RefreshCw, MessageCircle, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import consultasService from '@/services/consultasService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import DataTable from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const ESTADO = {
  open: { label: 'Nueva', variant: 'accent' },
  in_progress: { label: 'En proceso', variant: 'warning' },
  resolved: { label: 'Resuelta', variant: 'success' },
  closed: { label: 'Cerrada', variant: 'secondary' },
}

/** Chips de filtro. El filtro lo aplica el SERVIDOR (`status`), por eso no llevan conteo. */
const FILTROS = [
  { key: 'todos', label: 'Todas' },
  { key: 'open', label: 'Nuevas' },
  { key: 'in_progress', label: 'En proceso' },
  { key: 'resolved', label: 'Resueltas' },
  { key: 'closed', label: 'Cerradas' },
]

const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-SV', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—')

export default function ConsultorConsultas() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [consultas, setConsultas] = useState([])
  const [loading, setLoading] = useState(true)
  const filtroEstado = FILTROS.some((f) => f.key === params.get('estado')) ? params.get('estado') : 'todos'

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const filtro = filtroEstado && filtroEstado !== 'todos' ? { status: filtroEstado } : undefined
      const res = await consultasService.getConsultas(filtro)
      setConsultas(res?.data || [])
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error al cargar consultas')
    } finally {
      setLoading(false)
    }
  }, [filtroEstado])

  useEffect(() => { cargar() }, [cargar])

  const elegirFiltro = (k) => {
    const sig = new URLSearchParams(params)
    if (k === 'todos') sig.delete('estado'); else sig.set('estado', k)
    setParams(sig, { replace: true })
  }

  const abrir = (c) => navigate(`/consultor/consultas/${c.id}`)

  const columnas = [
    {
      clave: 'cliente', titulo: 'Cliente', prioridad: 'titulo',
      celda: (c) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{c.clientCompany || '—'}</p>
          {/* En el teléfono el asunto va debajo del cliente: es lo que se lee primero. */}
          <p className="line-clamp-2 text-body-sm text-subtle md:hidden">{c.title}</p>
        </div>
      ),
    },
    { clave: 'asunto', titulo: 'Asunto', prioridad: 'oculta', celda: (c) => <span className="text-foreground">{c.title}</span> },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (c) => {
        const e = ESTADO[c.status] || { label: c.status, variant: 'secondary' }
        return <Badge variant={e.variant}>{e.label}</Badge>
      },
    },
    { clave: 'actualizada', titulo: 'Actualizada', prioridad: 'meta', celda: (c) => <span className="text-subtle">{fecha(c.updatedAt)}</span> },
    {
      clave: 'accion', titulo: 'Acción', alineacion: 'derecha', prioridad: 'acciones',
      celda: (c) => (
        <Button variant="outline" size="xs" className="w-full md:w-auto"
          onClick={(ev) => { ev.stopPropagation(); abrir(c) }}>
          {c.status === 'open' || c.status === 'in_progress' ? 'Responder' : 'Abrir'} <ChevronRight size={15} />
        </Button>
      ),
    },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Consultas de clientes"
        description="Soporte técnico básico de tus clientes cd-2b: responde y resuelve."
        actions={
          <Button variant="ghost" size="icon" onClick={cargar} aria-label="Refrescar">
            <RefreshCw size={18} />
          </Button>
        }
      />

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Filtrar por estado">
        {FILTROS.map((f) => {
          const on = filtroEstado === f.key
          return (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => elegirFiltro(f.key)}
              className={cn(
                'inline-flex h-9 shrink-0 items-center rounded-full border px-3.5 text-body-sm font-medium transition-colors',
                on ? 'border-primary bg-primary text-white' : 'border-line bg-card text-foreground hover:border-neutral-300 dark:hover:border-neutral-600'
              )}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      <DataTable
        etiquetaTabla="Consultas de clientes"
        columnas={columnas}
        filas={consultas}
        claveDeFila={(c) => c.id}
        cargando={loading}
        tarjetasEnMovil
        onFilaClick={abrir}
        className="bg-card"
        vacio={
          <EmptyState
            icono={<MessageCircle size={24} />}
            titulo={filtroEstado === 'todos' ? 'Aún no tienes consultas de clientes' : 'No hay consultas con ese estado'}
            descripcion={filtroEstado === 'todos'
              ? 'Cuando un cliente tuyo pida ayuda desde su app, la verás aquí para responderle.'
              : 'Prueba con otro estado.'}
          />
        }
      />
    </PageContainer>
  )
}
